import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "../styles/appointments.scss";
import AppointmentService from "../service/AppointmentService";
import EventService from "service/event/EventService";
import UserService from "modules/user/service/UserService";
import {
  DASHBOARD_MODES,
  END_STATUSES,
  NAVIGO_STATUS,
  PAGE_SIZE,
  SERVICE_TYPE,
  STATUS_LABEL,
  STATUS_TONE,
} from "../helper/appointmentConstants";
import useStatusChange from "../hooks/useStatusChange";
import { formatDate, formatGenderAge, formatTimeSlot } from "../helper/formatters";
import StatusSelect from "./StatusSelect";
import Pagination from "./Pagination";
import ColumnFilter from "./ColumnFilter";
import UserMenu from "components/common/UserMenu";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobileAppointmentsScreen from "mobile/screens/MobileAppointmentsScreen";
import {
  PROFILE_TYPE_OPTIONS,
  profileTypeLabel,
} from "modules/patient/helper/patientConstants";
import {
  FILTER_KEY,
  statusOptionsFor,
  applyFilter,
  dayEndIso,
  dayStartIso,
} from "../helper/appointmentFilters";

/**
 * `filter` names the popover a column opens; a column without one draws no
 * funnel. The rule is that a funnel is only drawn where the SERVER can actually
 * filter — an unrecognised key is dropped silently by c.Bind, so a filter with
 * no field behind it fails OPEN: the funnel lights up and the rows never change.
 *
 * User type now HAS one. `ProfileTypes []int \`json:"profileTypes"\`` was added
 * to models.AppointmentFilters on 2026-08-03 (ZY-10251, b9b85efa) for this very
 * feature, and bridge has shipped against it since. It is on upstream's
 * development and release branches — not on the personal fork, which is three
 * months stale and is why an earlier check here concluded the field did not
 * exist.
 *
 * LANGUAGE still keeps none: it has no field, and nothing has changed for it.
 */
const COLUMNS = [
  { key: "userType", label: "User type", filter: "userType" },
  { key: "appointmentId", label: "Appointment ID" },
  { key: "patientId", label: "Patient ID" },
  { key: "patientName", label: "Patient name" },
  { key: "appointmentDate", label: "Appointment date", filter: "appointmentDate" },
  // No funnel: the Time slot filter was withdrawn 2026-09-08. The server only
  // honoured it when the appointment-date range collapsed to a single instant,
  // so it could only ever filter within one pinned day.
  { key: "timeSlot", label: "Time slot" },
  { key: "serviceName", label: "Service name", filter: "service" },
  { key: "accountCode", label: "Account code", filter: "accountCode" },
  { key: "status", label: "Status", filter: "status" },
];

/**
 * Split a column label so the funnel can be tied to its final word.
 *
 * `labelHead` is everything before the last space (with the space kept, so the
 * words still separate); `labelTail` is the last word, which is wrapped with the
 * funnel in a nowrap span. Single-word labels give an empty head and are
 * unaffected.
 */
const labelHead = (label) => {
  const i = String(label).lastIndexOf(" ");
  return i < 0 ? "" : String(label).slice(0, i + 1);
};
const labelTail = (label) => {
  const i = String(label).lastIndexOf(" ");
  return i < 0 ? String(label) : String(label).slice(i + 1);
};

/**
 * THE FILTER SURVIVES A TRIP OFF THIS SCREEN.
 *
 * Every route out of the dashboard comes back with
 * `history.push("/navigo-module")` — six push expressions across five screens
 * (PatientDetails.js:278, AppointmentDetails.js:467, ReviewPrescription.js:382,
 * HealthPlan.js:1201, CreateAppointment.js:1274 and its :1998 fallback), and
 * more ways to reach them than that: PatientDetails alone pushes from its
 * crumb, its back arrow and its error state. A push MOUNTS this component
 * again, so every useState re-initialised and the filter the user had just set
 * was gone. Opening a row and coming back meant picking the
 * whole thing again, every time (Nittin, 2026-09-12).
 *
 * Module scope, no provider and no context — the same shape bridge's
 * patients-v2 search uses for the same problem (its index.js:123) — and the
 * reason the fix lives HERE rather than at those eight call sites: browser Back
 * is a ninth entry point none of them would have covered, and the next screen
 * that pushes here would silently be a tenth.
 *
 * IN MEMORY ON PURPOSE. It survives navigation inside the SPA, which is the
 * complaint; a full reload starts clean, which is what a reload should do. That
 * also means nothing has to clear it on sign-out: logout is a FULL PAGE LOAD
 * (AuthenticationService.js:80, load-bearing and documented as such), and so is
 * login (Login.js:274) — so one operator's filter, which can hold a patient id
 * and an account code, cannot reach the next operator in the same tab. A
 * sessionStorage cache would have depended on that wipe firing without
 * exception; this depends on nothing.
 */
let lastFilter = null;

const AppointmentsDashboard = ({ history }) => {
  /* MOBILE ONLY. Everything mobile lives under src/mobile/; this line and the
     branch at the end of render are the whole of its footprint in this file.
     Called unconditionally, as a hook must be — MOBILE.appointmentsList gates
     the result, not the call. */
  const mobile = useIsMobile() && MOBILE.appointmentsList;

  const [appointments, setAppointments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);


  // Cancel flow (hooks/useStatusChange): a cancel-family status asks for a
  // reason, then posts — no refund choice; the server decides refunds, as on the
  // expert dashboard.

  /* Restored in the INITIALISER, not in an effect. An effect would run after
     the first render, so the fetch below would already have gone out unfiltered
     — the screen would flash every row and then refetch, and "the filter came
     back" would still look green. See lastFilter above.

     THE BOX IS SEEDED FROM THE RESTORED FILTER, not from what was last typed
     into it. Neither id column has a funnel (their COLUMNS entries carry no
     `filter` key), so the box is the ONLY on-screen evidence that an id search
     is applied — while `onChange` merely stores the digits and `searchBy`
     (Enter, or the Search button) is what reaches the wire. Parking the raw
     string would let the two disagree: search 17000, type 999 over it WITHOUT
     pressing Search, leave, come back, and the box would read 999 while the
     list is still filtered to 17000, with nothing on screen to say so. Before
     any of this the box came back empty and agreed with an unfiltered list, so
     that divergence would have been introduced by the restore itself.

     The cost is that an uncommitted draft is dropped on the way out, which is
     the right way round: there is no affordance here for "typed but not
     searched". The round trip is lossless because `onChange` strips non-digits
     and `searchBy` stores `Number(value)`. */
  const restoredSearch = (key) => {
    const v = lastFilter && lastFilter.filters && lastFilter.filters[key];
    return v ? String(v) : "";
  };
  const [appointmentIdInput, setAppointmentIdInput] = useState(() =>
    restoredSearch("appointmentId")
  );
  const [patientIdInput, setPatientIdInput] = useState(() => restoredSearch("patientId"));

  // Single source of server state: every fetch is driven by this object, and a
  // filter is cleared by deleting its key rather than sending a null.
  /* THE FILTER COMES BACK, THE PAGE DOES NOT — the same shape as bridge forcing
     `searching: false` over its restored search (its index.js:148), and for a
     harder reason than tidiness.

     The server does not clamp a page past the end: it appends
     `$skip (pageNum-1)*pageSize` unconditionally and counts totals from a
     pipeline that drops $skip/$limit (api-appointment-service
     repository/appointment.go:869-879 and :888-916), so page 3 of a list that
     is now 2 pages long answers 200 with an EMPTY array and a correct
     totalCount. This screen renders that as "No appointment found!" — which is
     a lie — and suppresses the Pagination bar with it, because that bar is gated
     on `appointments.length > 0` rather than on totalCount (the gate is further
     down this file). There is then no page control left to click and no way out
     but disturbing the filter we just preserved, or F5.

     It is also worth less than it looks: every fetch sends
     `statusOrderSort: true`, so any status change anywhere re-sorts the list and
     the row that was on page 3 often is not when you come back. The rule is
     already written down for the live filters — appointmentFilters.js:86-89,
     "any change resets to page 1. Without it, filtering while on page 12 asks
     for page 12 of a shorter list and shows an empty table" (appointmentFilters.js:87)
     — and a restore is that same change. */
  const [filters, setFilters] = useState(() =>
    lastFilter
      ? { ...lastFilter.filters, pageNum: 1 }
      : { pageNum: 1, pageSize: PAGE_SIZE, statusOrderSort: true }
  );

  const service = useRef(new AppointmentService()).current;

  /**
   * Whose appointments this screen shows (Nittin, 2026-07-30): the logged-in
   * provider's, not the tenant's.
   *
   * `state` is "resolving" | "ok" | "none". "none" means the account has no
   * provider identity at all — a pure ops employee, whose `doctorProfile.id` is
   * 0, or a doctor no provider row references. There is NO employee→provider
   * mapping anywhere in the backend (ConsultationIdentityDetails carries
   * doctorId and nothing else), so that is genuinely "no self", not "unknown".
   *
   * It FAILS CLOSED and says why. The alternative — falling back to the
   * unscoped list — would show a screen that says "My appointments" while
   * listing all 9k in the tenant, which is worse than showing nothing.
   */
  const [scope, setScope] = useState({ state: "resolving", ids: [] });

  useEffect(() => {
    let cancelled = false;
    service.resolveMyServiceProviderIds(
      (ids) => {
        if (cancelled) return;
        setScope(ids && ids.length ? { state: "ok", ids } : { state: "none", ids: [] });
      },
      () => {
        // A failed lookup is NOT "no providers" — widening on an error is the
        // exact silent-leak this screen must not have.
        if (!cancelled) setScope({ state: "error", ids: [] });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [service]);

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  /**
   * SCOPED to the logged-in provider via `serviceProviderID` (Nittin,
   * 2026-07-30) — the endpoint is tenant-wide only when that key is omitted,
   * verified live on dev1 — and to tele and in-person CONSULTATIONS via
   * `serviceMode` (Nittin, 2026-09-16; see DASHBOARD_MODES). Every column
   * filter composes on top of the scope; the scope keys are spread LAST and are
   * never one of the filters, so they cannot be filtered away.
   */
  useEffect(() => {
    // Nothing is fetched until we know WHOSE list to ask for — an unscoped
    // request would briefly paint the whole tenant before narrowing.
    if (scope.state !== "ok") {
      if (scope.state !== "resolving") {
        setAppointments([]);
        setTotalCount(0);
        setLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    service.fetchAppointments(
      { ...filters, serviceProviderID: scope.ids, serviceMode: DASHBOARD_MODES },
      (res) => {
        if (cancelled) return;
        setAppointments((res && res.appointments) || []);
        setTotalCount((res && res.totalCount) || 0);
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        setAppointments([]);
        setTotalCount(0);
        setLoading(false);
        showError(`Could not load appointments: ${(err && err.message) || ""}`);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [filters, scope, service, showError]);

  /**
   * What the popovers show. Kept separate from `filters` because the two speak
   * different languages: a date popover holds "2026-07-01" while the server
   * wants an ISO instant, and a Status option holds one label that expands to
   * TWO status codes (a legacy 0 and a current 5 both mean "Scheduled", and
   * real rows carry both). Deriving the popover state back out of the request
   * body would mean inverting those mappings, which is where they drift.
   */
  /* BOTH halves are restored or neither: `filters` alone would leave the
     funnels looking clear over a filtered request, and `ui` alone would light
     up funnels the server knows nothing about. */
  const [ui, setUi] = useState(() => (lastFilter && lastFilter.ui) || {});

  /* Saved on the way OUT rather than on every change — this is the screen as it
     was last looked at. A ref so the unmount cleanup sees the LAST values and
     not the first render's closure, and an empty dep list so it fires on unmount
     only.
     Deliberately NOT saved: `loading`, `appointments`, `totalCount` and the
     cancel dialog state. A request in flight when the screen unmounted is
     not in flight when it comes back, and the rows are re-read anyway — an
     appointment list is too volatile to re-show from memory, unlike bridge's
     patient search, which restores its results because nothing re-runs the
     search for it.

     React 16 with no StrictMode (package.json, and index.js renders <App/>
     bare), so this cleanup fires exactly ONCE. On React 18 under StrictMode the
     simulated mount/unmount would run it too and park a copy of the defaults —
     harmless here, since the next real unmount overwrites it, but it is the
     thing to check if this app ever moves. */
  const latest = useRef({});
  /* The two id boxes are NOT parked — they are derived from `filters` on the way
     back in (see restoredSearch above), so storing them too would only create a
     second source of truth for the same thing. */
  latest.current = { filters, ui };
  useEffect(
    () => () => {
      lastFilter = { ...latest.current };
    },
    []
  );

  /* The Status filter offers exactly what this user's status dropdown offers —
     see statusOptionsFor. Read once: the signed-in user cannot change without
     a full reload. */
  const statusOptions = useMemo(() => statusOptionsFor(new UserService().doctorCategory()), []);

  const setColumnFilter = (col, value) => {
    setUi((prev) => {
      const { [col]: _omit, ...rest } = prev;
      return value == null ? rest : { ...rest, [col]: value };
    });

    if (col === "status") {
      const codes = (value || []).reduce(
        (all, label) =>
          all.concat((statusOptions.find((o) => o.id === label) || { codes: [] }).codes),
        []
      );
      setFilters((prev) => applyFilter(prev, FILTER_KEY.status, value ? codes : null));
      return;
    }

    /* Three plain multi-selects. Their column names ARE their FILTER_KEY
       names, and applyFilter drops an empty array (and resets to page 1) for
       all of them — which is how an emptied User type filter stops sending
       profileTypes at all rather than sending []. */
    if (col === "service" || col === "accountCode" || col === "userType") {
      setFilters((prev) =>
        applyFilter(prev, FILTER_KEY[col], value && value.length ? value : null)
      );
      return;
    }

    if (col === "appointmentDate") {
      /* The time-slot coupling that used to live here went with that filter:
         there is no longer a timeSlot key to drop when the range widens. */
      /* AN EMPTY BOUND IS OMITTED, NOT SENT EMPTY. Both helpers answer "" for a
         blank date, and the server's DateRange.From/To are time.Time — which
         cannot unmarshal "", so picking From and leaving To blank 400'd the
         whole filter request with an unmarshalling error. Bridge omits the
         bound, which is why the same window worked there. The repository tests
         each bound with IsZero() and adds only the comparison it was given, so
         one-sided is a valid window server-side. */
      const from = value ? dayStartIso(value.from) : "";
      const to = value ? dayEndIso(value.to) : "";
      const range =
        from || to
          ? Object.assign({}, from ? { from } : null, to ? { to } : null)
          : null;
      setFilters((prev) => applyFilter(prev, FILTER_KEY.appointmentDate, range));
    }
  };

  /**
   * Option loaders for the two async funnels. Both mirror bridge's proven
   * request (`pageNum: 0, pageSize: 0` = unpaginated) and search server-side —
   * these endpoints exist in phr but had never been called, so the bodies stay
   * byte-identical to the ones bridge is known to get answers from.
   */
  const loadServices = useCallback(
    (q, cb) =>
      service.getServices(
        // The search field is `serviceName`, not `name` (Nittin, 2026-07-30).
        // Consultation services only: the list shows nothing else, so a lab
        // test here could only ever filter it down to an empty table. The
        // catalogue's own serviceTypeId is always set (unlike an appointment's).
        {
          pageNum: 0,
          pageSize: 0,
          nameMatch: [],
          serviceName: q || "",
          serviceTypeIds: [SERVICE_TYPE.CONSULTATION],
        },
        (res) =>
          cb(
            ((res && res.services) || [])
              .filter((x) => x && x.id != null)
              // `serviceName` on BOTH sides — it is what the query searches and
              // what the table's Service name column shows. `name` is the
              // service CODE ("ML12"), so labelling with it listed codes while
              // searching names, and the two never lined up (Nittin, 2026-07-30).
              .map((x) => ({ id: x.id, label: x.serviceName || x.name || `Service ${x.id}` }))
          ),
        () => cb([])
      ),
    [service]
  );

  const loadAccounts = useCallback(
    (q, cb) =>
      service.getAccounts(
        { pageNum: 0, pageSize: 0, accountCodeMatch: [], accountCode: q || "" },
        (res) =>
          cb(
            ((res && res.accounts) || [])
              .filter((x) => x && x.accountCode)
              // The code IS the id here — the filter sends accountCode strings,
              // not numeric ids.
              /* "(CODE) Name" — the Create appointment screen's format (product,
                 2026-09-10). The name is `accountName` on this response
                 (api-client-config-service models/entity.go Accounts); there is no
                 `name`, which is what this read, so every option was a bare code. */
              .map((x) => ({ id: x.accountCode, label: x.accountName ? `(${x.accountCode}) ${x.accountName}` : x.accountCode }))
          ),
        () => cb([])
      ),
    [service]
  );

  const searchBy = (key, raw) => {
    const value = String(raw || "").trim();
    setFilters((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return value
        ? { ...rest, [key]: Number(value), pageNum: 1 }
        : { ...rest, pageNum: 1 };
    });
  };

  /* EMPTYING THE BOX DROPS ITS FILTER, with no need to press Search (Nittin,
     2026-09-23). Neither id column has a funnel, so the box is the only thing
     on screen that says an id search is applied: clearing it and leaving the
     list filtered to 17653 left the screen claiming "No appointment found!"
     against an empty search box, with no control left to undo it but retyping
     the id and searching for it again.

     Only the CLEARED case is applied on the fly. A box that still has digits in
     it stays a draft until Search or Enter commits it — that is the existing
     contract, and the restore above depends on it. The guard is on the filter
     actually being applied, so typing into an already-empty box does not
     refetch: setFilters always returns a new object, which the fetch effect
     would take as a change. */
  const changeIdInput = (key, setInput, raw) => {
    const digits = raw.replace(/\D/g, "");
    setInput(digits);
    if (!digits && filters[key] != null) searchBy(key, "");
  };

  /* Re-requests the SAME page on purpose, and does NOT clamp.
  
     This is the root of how an out-of-range page is reached without anyone
     paging there: a status change on the last page removes that row from the
     filtered set, onApplied calls this, the total drops, and the page the
     operator is standing on is now past the end.
  
     Clamping here was considered and rejected. Moving someone to a different
     page as a side effect of changing one row's status is a surprise in the
     middle of a different task, it costs a second request and a second loading
     flash right after the one the status change already caused, and it would
     have to be careful not to fight ordinary paging. The empty page is also
     informative — "the row you just changed has left this filter" is a real
     thing to be told — and with the bar now gated on the total rather than on
     this page's rows, page 1 is one click away. So the screen states the
     situation and leaves the move to the operator. */
  const refetch = () => setFilters((prev) => ({ ...prev }));

  /* THE SHARED FLOW. Every screen with a status control uses this same hook,
     so the cancel sequence cannot drift between them. */
  const { changeStatus, updatingId, dialogs } = useStatusChange({
    // Refetch rather than patching locally: the server re-sorts by status.
    onApplied: () => refetch(),
    onError: showError,
  });

  const goToPage = (pageNum) => setFilters((prev) => ({ ...prev, pageNum }));

  /* The mobile arrangement of everything computed above. Placed after the last
     hook so hook order is identical on both paths. It hands over state and
     callbacks only — every filter control over there calls the same searchBy /
     setColumnFilter / changeStatus these desktop controls call. The desktop
     tree below is unreachable on mobile and unchanged by it. */
  if (mobile) {
    return (
      <MobileAppointmentsScreen
        appointments={appointments}
        totalCount={totalCount}
        loading={loading}
        scope={scope}
        filters={filters}
        ui={ui}
        onColumnFilter={setColumnFilter}
        appointmentIdInput={appointmentIdInput}
        patientIdInput={patientIdInput}
        onSearchBy={searchBy}
        onSetAppointmentIdInput={setAppointmentIdInput}
        onSetPatientIdInput={setPatientIdInput}
        updatingId={updatingId}
        onChangeStatus={changeStatus}
        onGoToPage={goToPage}
        onOpenPatient={(to) => history.push(to)}
        loadServices={loadServices}
        loadAccounts={loadAccounts}
        dialogs={dialogs}
      />
    );
  }

  return (
    <div className="appointments-page">
      <header className="appointments-header">
        <h1>My appointments</h1>
        {/* This screen renders no TopBar, so without the account menu here
            there is no way to log out of the screen you land on. */}
        <div className="appointments-header-end">
          <button
            className="add-appointment-btn"
            type="button"
            onClick={() => history.push("/navigo-module/create")}
          >
            <span className="plus">+</span> Book appointment
          </button>
          <UserMenu />
        </div>
      </header>

      <div className="search-row">
        <div className="search-field">
          <label htmlFor="appointment-id">Appointment ID</label>
          <div className="search-control">
            <input
              id="appointment-id"
              type="text"
              placeholder="e.g. 3572"
              value={appointmentIdInput}
              maxLength={8}
              onChange={(e) =>
                changeIdInput("appointmentId", setAppointmentIdInput, e.target.value)
              }
              onKeyDown={(e) =>
                e.key === "Enter" && searchBy("appointmentId", appointmentIdInput)
              }
            />
            <button
              type="button"
              className="search-btn"
              onClick={() => searchBy("appointmentId", appointmentIdInput)}
            >
              <SearchIcon /> Search
            </button>
          </div>
        </div>

        <div className="search-field">
          <label htmlFor="patient-id">Patient ID</label>
          <div className="search-control">
            <input
              id="patient-id"
              type="text"
              placeholder="e.g. 158426"
              value={patientIdInput}
              maxLength={8}
              onChange={(e) =>
                changeIdInput("patientId", setPatientIdInput, e.target.value)
              }
              onKeyDown={(e) =>
                e.key === "Enter" && searchBy("patientId", patientIdInput)
              }
            />
            <button
              type="button"
              className="search-btn"
              onClick={() => searchBy("patientId", patientIdInput)}
            >
              <SearchIcon /> Search
            </button>
          </div>
        </div>
      </div>

      <div className="appointments-table">
        <div className="table-head">
          {COLUMNS.map((col) => (
            <div className="th" key={col.key}>
              {/* The funnel flows INSIDE the label, bound to its LAST WORD by a
                  nowrap tail.

                  Two bugs to avoid, and they pull in opposite directions. As a
                  sibling flex item the funnel sat after the span's BOX, which a
                  WRAPPED label leaves wider than its longest line — 38px adrift
                  on "Appointment date", 34px on "Account code". Merely moving it
                  inside the span fixed that but let it break onto a line of its
                  own, because "APPOINTMENT DATE" fits the track while
                  "APPOINTMENT DATE + funnel" does not.
                  Tying it to the last word makes the break fall before that word
                  instead: "APPOINTMENT / DATE ⧩". */}
              <span>
                {labelHead(col.label)}
                <span className="th-tail">
                  {labelTail(col.label)}
                {col.filter && (
                <ColumnFilter
                  label={col.label}
                  mode={
                    col.filter === "appointmentDate" ? "dateRange" : "checkbox"
                  }
                  options={
                    col.filter === "status"
                      ? statusOptions
                      : col.filter === "userType"
                      ? PROFILE_TYPE_OPTIONS
                      : undefined
                  }
                  loadOptions={
                    col.filter === "service"
                      ? loadServices
                      : col.filter === "accountCode"
                      ? loadAccounts
                      : undefined
                  }
                  value={ui[col.filter]}
                  onApply={(v) => setColumnFilter(col.filter, v)}
                />
                )}
                </span>
              </span>
            </div>
          ))}
        </div>

        {(loading || scope.state === "resolving") && (
          <div className="table-message">Loading appointments&hellip;</div>
        )}

        {/* An empty scoped list has FOUR very different causes and each says so.
            "No appointment found" is only correct when the scope resolved and the
            query genuinely matched nothing — not merely when this PAGE is
            empty, which is the fourth cause below. */}
        {scope.state === "none" && (
          <div className="table-message is-note">
            This screen shows appointments for the provider you are signed in as,
            and your account isn&rsquo;t linked to a provider profile &mdash; so there
            is nothing to list. Ask an admin to link your doctor profile to a
            service provider.
          </div>
        )}

        {scope.state === "error" && (
          <div className="table-message is-note">
            Couldn&rsquo;t confirm which provider you are signed in as, so no
            appointments are shown &mdash; rather than showing everyone&rsquo;s.
            Reload to try again.
          </div>
        )}

        {!loading && scope.state === "ok" && appointments.length === 0 && totalCount === 0 && (
          <div className="table-message">No appointment found!</div>
        )}

        {/* PAST THE END OF THE RESULTS, which is not the same as having none.
            The server does not clamp: it appends `$skip (pageNum-1)*pageSize`
            unconditionally and counts the total from a pipeline that drops
            $skip/$limit (api-appointment-service repository/appointment.go:869-879
            and :888-916), so page 3 of a list that is now 2 pages long answers
            200 with an empty array and a correct, non-zero totalCount. Saying
            "No appointment found!" there is simply false — there are
            appointments, just not on this page — and the page bar below is what
            gets you back to them. */}
        {!loading && scope.state === "ok" && appointments.length === 0 && totalCount > 0 && (
          <div className="table-message">
            Nothing on this page &mdash; the list got shorter. Pick a page below.
          </div>
        )}

        {!loading &&
          appointments.map((a) => (
            /* The row is INERT. Navigation out of this table is the two id
               links below and nothing else — a whole-row target made every
               cell a nav trap, so reading an account code or opening the
               status dropdown could throw you onto another screen. */
            <div className="tr" key={a.id}>
              <div className="td user-type td-type">
                {/* The type was conveyed by COLOUR ALONE and hidden from
                    assistive tech outright, which makes a filter for it
                    unusable: you could select "QA user" and have no way to tell
                    which rows came back. */}
                {(() => {
                  const typeLabel = profileTypeLabel(a.patientProfileType);
                  return (
                    <span
                      className={`avatar profile-type-${a.patientProfileType || 0}`}
                      /* Labelled only when the row actually carries a type.
                         Untyped rows stay hidden from assistive tech rather
                         than being announced as something they are not. */
                      {...(typeLabel
                        ? { role: "img", title: typeLabel, "aria-label": typeLabel }
                        : { "aria-hidden": "true" })}
                    />
                  );
                })()}
              </div>
              {/* The second class on each cell NAMES it. Desktop ignores them
                  entirely — every desktop rule selects .td, .tr or the grid
                  track, and none of these names appears anywhere else in src.
                  They exist so the mobile card layout can place cells by
                  meaning instead of :nth-child: this table has had its column
                  order changed twice and a column removed, and a positional
                  selector would have silently re-pointed each time. */}
              {/* The .ap-line wrappers group the cells into the mobile card's four
                  lines. They are INVISIBLE TO DESKTOP: `.ap-line { display:
                  contents }` outside the media query, so the grid still sees the
                  .td cells as its own children and lays them out exactly as
                  before. Only inside the mobile query do they become real flex
                  rows — which is what lets the card reorder cells AND break
                  lines, neither of which plain inline or plain flex can do
                  alone. Cell ORDER in the DOM is unchanged. */}
              <div className="ap-line ap-line-ids">
                <div className="td td-appt">
                  <Link
                    className="cell-link"
                    to={`/navigo-module/appointment/${a.id}`}
                  >
                    {a.id}
                  </Link>
                </div>
                <div className="td td-pid">
                  {/* Keyed by the APPOINTMENT id, not the patient's own:
                      App.js routes this screen as
                      /navigo-module/patient/:appointmentId. The cell DISPLAYS a
                      patient id — the two must not be reconciled. */}
                  <Link
                    className="cell-link"
                    to={`/navigo-module/patient/${a.id}`}
                  >
                    {/* FAMILY BOOKINGS: `bookedFor` is the member the visit is
                        for; `patientID` is the account holder who booked it.
                        This column showed the account holder for both, so two
                        rows booked by one parent for two children were
                        indistinguishable — and the id shown did not match the
                        patient named beside it, because the server overwrites
                        patientName with the member's
                        (repository/appointment.go:911-916, gated on
                        `BookedFor != 0`).

                        Same rule as bridge's column
                        (NavigoDashboard.js:1678) and as phr's own appointment
                        -details screen (AppointmentDetails.js:491), written the
                        way that screen writes it so the two cannot drift.

                        Deliberately NOT following phr's "Update booking for"
                        (the appointment's `updatedBookingFor`): this list shows
                        appointments as BOOKED. The patient screen and
                        appointment details show the switched member, each with
                        an "Original appointment" pill naming this one.
                        `bookedFor` is a plain int64 with no omitempty, so 0 —
                        not absent — is the "no member" case. */}
                    {a.bookedFor && a.bookedFor !== 0 ? a.bookedFor : a.patientID}
                  </Link>
                </div>
              </div>
              <div className="ap-line ap-line-name">
                <div className="td patient-name td-name">
                  {a.patientName}
                  <span className="gender-age">
                    {formatGenderAge(a.patientGender, a.patientAge)}
                  </span>
                </div>
              </div>
              <div className="ap-line ap-line-when">
                <div className="td td-date">{formatDate(a.appointmentDate)}</div>
                <div className="td td-slot">
                  {formatTimeSlot(a.timeSlot, a.serviceDuration)}
                </div>
              </div>
              <div className="ap-line ap-line-what">
                <div className="td td-service">{a.serviceName}</div>
                <div className="td td-account">
                  {a.accountCode && <span className="chip">{a.accountCode}</span>}
                </div>
              </div>
              {/* Stop the row's click here: this cell holds a live dropdown
                  that opens the cancel flow, and navigating mid-flow
                  would abandon it. */}
              <div className="td td-status">
                <StatusSelect
                  value={a.status}
                  tone={STATUS_TONE[a.status] || "progress"}
                  label={STATUS_LABEL[a.status] || "—"}
                  labels={STATUS_LABEL}
                  tones={STATUS_TONE}
                  disabled={updatingId === a.id}
                  // Terminal statuses are a dead end server-side, so show a
                  // plain pill rather than a dropdown that can only error.
                  readOnly={END_STATUSES.indexOf(a.status) !== -1}
                  onChange={(next) => changeStatus(a, next)}
                />
              </div>
            </div>
          ))}
      </div>

      {dialogs}

      {/* GATED ON THE TOTAL, not on this page's rows. Keyed on
          `appointments.length` it vanished exactly when it was needed most — on
          a page past the end there were no rows, so the control that would have
          taken you back to page 1 was the thing that disappeared, and the only
          escapes left were re-applying a filter, an id search or F5. Pagination
          computes everything from totalCount and always offers page 1, so it
          renders usefully for an empty page. */}
      {!loading && totalCount > 0 && (
        <Pagination
          pageNum={filters.pageNum}
          pageSize={filters.pageSize}
          totalCount={totalCount}
          onChange={goToPage}
        />
      )}
    </div>
  );
};

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" className="icon" aria-hidden="true">
    <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <line
      x1="10.8"
      y1="10.8"
      x2="14.5"
      y2="14.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);


AppointmentsDashboard.propTypes = {
  history: PropTypes.object.isRequired,
};

export { NAVIGO_STATUS };
export default AppointmentsDashboard;
